import { useContext } from "react";
import { ViewRequest, getEligibilityBasedQueryKey, getViewRequest, request } from "./request";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { useQuery } from "@tanstack/react-query";
import { AuthenticationContext } from "../context";

export interface ExternalConnectionListRequest {
  readonly location?: string;
  readonly current_values?: { [key: string]: string };
}

export interface ExternalConnectionLink {
  readonly id: number;
  readonly label: string;
  readonly dashboard_icon?: string;
  readonly menu_icon?: string;
  readonly on_dashboard?: boolean;
  readonly helper_text?: string;
  readonly link_text?: string;
}

export interface ExternalConnectionListResponse {
  readonly results: {
    readonly link: ExternalConnectionLink;
  }[];
  readonly result_count: string;
}

export const listExternalConnections = async (authToken: string, body: ExternalConnectionListRequest) => {
  return request<ExternalConnectionListRequest, ExternalConnectionListResponse>({
    endpoint: "/user/connection/list",
    body: { ...body },
  }, authToken);
};

export const useListExternalConnectionsQuery = (body?: ExternalConnectionListRequest) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listExternalConnections(await getAuthToken(), {
          ...(body || {}),
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listExternalConnections(await getAuthToken(), {
          ...(body || {}),
        });
      }
    },
    queryKey: ["externalConnectionList", ...getEligibilityBasedQueryKey(memberEligibility)],
    staleTime: Infinity,
  });

  return query;
};

export interface ExternalConnectionGetRequest {
  readonly id: number;
  readonly location?: string;
}

export interface ExternalConnectionGetResponse {
  readonly saml_response: string;
  readonly relay_state: string;
  readonly destination: string;
}

export const getExternalConnection = async (authToken: string, body: ExternalConnectionGetRequest & Partial<ViewRequest>) => {
  return request<ExternalConnectionListRequest, ExternalConnectionGetResponse>({
    endpoint: "/user/connection/get",
    body: { ...body },
  }, authToken);
};

export const useGetExternalConnectionQuery = (filters: ExternalConnectionGetRequest, enabled?: boolean) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["externalConnectionGet", filters.id, ...getEligibilityBasedQueryKey(memberEligibility)];

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getExternalConnection(await getAuthToken(), {
          ...filters,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return getExternalConnection(await getAuthToken(), {
          ...filters,
        });
      }
    },
    queryKey: queryKey,
    enabled: enabled !== false,
  });

  return query;
};
